import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import * as moment from 'moment';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { TimesheetService } from 'src/app/_services/timesheet.service';
import { BaseComponent } from '../../components/base/base.component';

//Validazione personalizzata
export const valueLessThanOrEqualToBillingValidator: ValidatorFn = (
  group: AbstractControl
): ValidationErrors | null => {
  const billing = group.get('billing_value')?.value;
  const value = group.get('value')?.value;

  if (!billing || !value) return null;

  const billingDate = new Date(billing);
  const valueDate = new Date(value);

  return valueDate <= billingDate ? null : { valueGreaterThanBilling: true };
};

@Component({
  selector: 'app-modal-request-timesheet',
  templateUrl: './modal-request-timesheet.component.html',
  styleUrls: ['./modal-request-timesheet.component.css']
})
export class ModalRequestTimesheetComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input() projects: any[] = [];
  @Input() dateHeader: string = '';
  @Input() note: string = '';
  @Input() totaliTimesheet: any;
  @Input() isRequest: boolean = false;
  @Input() chosenUser: string = '';
  @Input() profileData: any;
  @Output() notifyParent: EventEmitter<any> = new EventEmitter();

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _modalRef: NzModalRef = inject(NzModalRef);
  private readonly _timesheetService: TimesheetService =
    inject(TimesheetService);

  timeSheetForm: FormGroup;
  timeSheetsModal: any[] = [];
  selectedTotal: any;
  isReadOnly: boolean = false;
  isModalRequired: boolean = false;
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor() {
    super();
  }

  ngOnInit() {
    this.buildForm();
    this.updateTotalForm();
  }

  /**
   * Funzione per creare e inizializzare il form di richiesta delle ore su TimeSheet
   */
  _initForm = () => {
    this.timeSheetForm = this._fb.group({
      date: ['', Validators.required],
      projects: this._fb.array([]),
      note: [this.note]
    });
  };

  buildForm() {
    this._initForm();
    this.timeSheetForm.patchValue({
      date: this.dateHeader
    });

    const formArrayName = this.timeSheetForm.get('projects') as FormArray;

    const filteredData = this.projects.filter((key: any) => {
      const selectedDayData = key.days.find(
        (d: any) => d.date === this.dateHeader
      );
      return (
        selectedDayData?.value != null && selectedDayData?.billing_value != null
      );
    });

    this.timeSheetsModal = filteredData;
    filteredData.forEach((key: any) => {
      const selectedDayData = key.days.find(
        (d: any) => d.date === this.dateHeader
      );
      if (selectedDayData.note)
        this.timeSheetForm.patchValue({
          note: this.note
        });

      const group = this._fb.group(
        {
          project_id: key.id,
          billing_value: selectedDayData?.billing_value,
          value: selectedDayData?.value,
          initial_value: selectedDayData?.value,
          initial_billing_value: selectedDayData?.billing_value,
          is_authorized: selectedDayData.is_authorized,
          status: selectedDayData.status
        },
        {
          validators: [valueLessThanOrEqualToBillingValidator]
        }
      );

      // Quando cambia billing_value
      group.get('billing_value')?.valueChanges.subscribe(billingRaw => {
        const scrumRaw = group.get('value')?.value;

        const billing = billingRaw as Date;
        const scrum = scrumRaw as Date;

        if (!billing || !scrum) return;

        if (this.compareTimes(billing, scrum) < 0) {
          group.get('value')?.setValue(billing, { emitEvent: false });
        }
      });

      // Quando cambia scrum (value)
      group.get('value')?.valueChanges.subscribe(scrumRaw => {
        const billingRaw = group.get('billing_value')?.value;

        const scrum = scrumRaw as Date;
        const billing = billingRaw as Date;

        if (!scrum || !billing) return;

        if (this.compareTimes(scrum, billing) > 0) {
          group.get('billing_value')?.setValue(scrum, { emitEvent: false });
        }
      });

      if (!this.isRequest) {
        group.get('billing_value')?.valueChanges.subscribe(() => {
          this.updateTotalForm();
        });

        group.get('value')?.valueChanges.subscribe(() => {
          this.updateTotalForm();
        });

        this.isReadOnly = false;
      } else {
        this.isReadOnly = true;
      }
      formArrayName.push(group);
    });
    this.isModalRequired = true;
  }

  get projectsControl() {
    const projects = this.timeSheetForm?.get('projects');
    return projects instanceof FormArray ? projects.controls : [];
  }

  /**
   * questa funzione serve per convertire una stringa (tempo) in una data
   * @param timeString stringa (tempo) da convertire in data
   * @returns ritorna un tipo data
   */
  convertToTimeObject(timeString: string): Date | null {
    if (
      !timeString ||
      typeof timeString !== 'string' ||
      !timeString.includes(':')
    )
      return null; // Gestione del valore nullo

    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0); // Imposta solo ore e minuti
    return date;
  }

  private compareTimes(t1: string | Date, t2: string | Date): number {
    const m1 = this.timeToMinutes(t1);
    const m2 = this.timeToMinutes(t2);
    return m1 - m2;
  }

  /**
   * Funzione che serve per trasformare il tempo impostato in minuti
   * @param time
   * @returns
   */
  private timeToMinutes(time: string | Date): number {
    if (!time) return 0;

    if (typeof time === 'string') {
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m;
    }

    return time.getHours() * 60 + time.getMinutes();
  }

  /**
   * Funzione che serve per aggiuornare i totali nella modale
   */
  updateTotalForm() {
    const projectsArray = this.timeSheetForm.get('projects') as FormArray;

    const valueTimes: Array<Date | string> = projectsArray.controls.map(
      group => group.get('value')?.value
    );

    const billingTimes: Array<Date | string> = projectsArray.controls.map(
      group => group.get('billing_value')?.value
    );

    this.selectedTotal = {
      value: this.sumTimes(valueTimes),
      billing_value: this.sumTimes(billingTimes)
    };
  }

  /**
   * funzione che serve per sommare le ore di una giornata
   * @param times array delle ore impostate
   * @returns string totale delle ore
   */
  sumTimes(times: Array<Date | string>): string {
    const totalMinutes = times.reduce(
      (acc, time) => acc + this.timeToMinutes(time),
      0
    );

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  isBillingValueDisabled(project: AbstractControl): boolean {
    return !project.get('billing_value')?.value;
  }

  isValueDisabled(project: AbstractControl): boolean {
    return !project.get('value')?.value;
  }

  /**
   * Funzione che serve per salvare la richiesta delle ore
   */
  saveRequest = () => {
    this.timeSheetForm.value.projects.forEach((project: any) => {
      project.billing_value = moment(project.billing_value).format('HH:mm');
      project.value = moment(project.value).format('HH:mm');
    });

    this._timesheetService
      .requestTimesheet(this.getModifiedProjects())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.responseToast('success', res.message);
          this.isModalRequired = false;
          this.notifyParent.emit();
          this._modalRef.close();
        },
        error: (err: any) => {
          this.responseToast('error', this.getTimesheetErrorMessage(err));
        }
      });
  };

  /**
   * Funzione che serve per recuperare solo i progetti in cui sono state modificate le ore
   */
  getModifiedProjects(): any {
    const modifiedProjects: any[] = [];
    const projectsArray = this.timeSheetForm.get('projects') as FormArray;

    projectsArray.controls.forEach((group: AbstractControl) => {
      const formGroup = group as FormGroup;
      const value = this.formatTimeToString(formGroup.get('value')?.value);
      const initialValue = this.formatTimeToString(
        formGroup.get('initial_value')?.value
      );

      const billing = this.formatTimeToString(
        formGroup.get('billing_value')?.value
      );
      const initialBilling = this.formatTimeToString(
        formGroup.get('initial_billing_value')?.value
      );

      if (value !== initialValue || billing !== initialBilling) {
        modifiedProjects.push({
          project_id: formGroup.value.project_id,
          billing_value: billing,
          value: value
        });
      }
    });
    return {
      user_id: this.chosenUser || this.profileData?.id,
      projects: modifiedProjects,
      note: this.timeSheetForm.value.note,
      date: this.dateHeader
    };
  }

  private formatTimeToString(date: Date | string): string {
    if (typeof date === 'string') return date;

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Funzione che serve per confermare la richiesta
   */
  confirmRequest(userId: string, date: string) {
    const allProjects = this.timeSheetForm?.get('projects')?.value || [];

    /**
     * recupero i soli progetti per cui l'utente è autorizzato ad accettare la richiesta,
     * per cui è presente una richiesta in attesa nel giorno selezionato
     */
    const filteredProjects = allProjects.filter((project: any) => {
      const modalProject = this.timeSheetsModal.find(
        (p: any) => p.id === project.project_id
      );

      if (!modalProject) return false;

      // Trova il giorno selezionato in quel progetto
      const selectedDay = modalProject.days.find(
        (day: any) => day.date === this.dateHeader
      );

      return project.is_authorized && selectedDay?.status === 'in_attesa';
    });

    const obj = {
      user_id: userId,
      date: date,
      projects: filteredProjects
    };

    this._timesheetService
      .confirmRequest(obj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.responseToast('success', res.message);
          this.notifyParent.emit();
          this._modalRef.close();
        },
        error: (err: any) => {
          this.responseToast('error', this.getTimesheetErrorMessage(err));
        }
      });
  }

  private getTimesheetErrorMessage(err: any): string {
    return err?.error?.error ?? err?.error?.message ?? 'Errore timesheet.';
  }

  /**
   * Funzione che alla modifica delle ore all'interno della modale di richiesta,
   * permette di verificare che il numero di ore inserite, non siano maggiori del limite massimo: 8
   *
   * Return true se c'è almeno un progetto che supera 8 ore
   * Return false se nessun progetto supera 8 ore
   */
  hasFinishMaxHours(): boolean {
    const projects = this.timeSheetForm.value.projects || [];

    for (const project of projects) {
      const billingMoment = moment(project.billing_value, 'HH:mm');
      const valueMoment = moment(project.value, 'HH:mm');

      const billingHours = billingMoment.hours() + billingMoment.minutes() / 60;
      const valueHours = valueMoment.hours() + valueMoment.minutes() / 60;

      if (billingHours > 8 || valueHours > 8) {
        return true; // c'è almeno un progetto che supera 8 ore
      }
    }
    return false; // nessun progetto supera 8 ore
  }

  closeModal() {
    this.isModalRequired = false;
    this.dateHeader = '';
    this.selectedTotal = null;
    this._initForm(); // reset completo
    this._modalRef.close();
  }

  /**
   * Funzione che serve per disabilitare le ore
   * @returns array: number
   */
  disabledHours(): number[] {
    return [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  }

  /**
   * funzione che serve per disabilitare i minuti in base alle ore selezionate
   * @param hour ora selezionata
   * @returns array
   */
  disabledMinutes(hour: number): number[] {
    if (hour == 8) {
      return Array.from({ length: 60 }, (_, i) => i + 1);
    }
    return [];
  }

  /**
   * Metodo che serve per controllare se l'utente ha almeno un progetto dove può modificare le ore dalla home
   * @returns boolean
   */
  haveUthorizedProjects(): boolean {
    return this.projects.some(project =>
      project.days?.some((day: any) => day.is_authorized === true)
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
